import { useState } from 'react';
import {
  Avatar,
  Button,
  Pagination,
  Segmented,
  Select,
  Space,
  Input,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import { PlusOutlined, SearchOutlined, EyeOutlined, EditOutlined } from '@ant-design/icons';
import { DataTable } from '../../../components/DataTable';
import { EmptyState } from '../../../components/EmptyState';
import { DateRangeFilter } from '../../../components/DateRangeFilter';
import { KpiRow } from '../../dashboard/components/KpiRow';
import { KpiCard } from '../../dashboard/components/KpiCard';
import { useExcepcionesList, useExcepcionesStats } from '../api/useExcepciones';
import { useUsersList } from '../../users/api/useUsers';
import { useHasPermission } from '../../../auth/useHasPermission';
import { useT } from '../../../locales';
import { ExcepcionCreateModal } from './ExcepcionCreateModal';
import { ExcepcionDetailModal } from './ExcepcionDetailModal';
import { ExcepcionEditModal } from './ExcepcionEditModal';
import dayjs from 'dayjs';
import { formatCurrency, Permission, type CobroExcepcionResponse } from '@aid/shared';
import { excepcionAmount } from '../lib/excepcionAmount';
import { DATE_FORMAT } from '../../../lib/date';
import { serverSortProps, useServerPaginacion } from '../../../lib/server-sort';

const { Text } = Typography;

type GroupBy = 'none' | 'cliente' | 'gestor';

function getInitials(nombre: string | null | undefined): string {
  const words = nombre?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (words.length === 0) return '#';
  return words
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

export function ExcepcionesTable() {
  const t = useT();
  const { token } = theme.useToken();
  const [idCliente, setIdCliente] = useState<string>('');
  const [idUsuario, setIdUsuario] = useState<string>('');
  const [fechaDesde, setFechaDesde] = useState<string | undefined>(undefined);
  const [fechaHasta, setFechaHasta] = useState<string | undefined>(undefined);
  const { page, pageSize, sort, handleChange, setPage } =
    useServerPaginacion<CobroExcepcionResponse>(20);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [groupBy, setGroupBy] = useState<GroupBy>('none');

  const { data, isLoading, error, refetch } = useExcepcionesList({
    limit: pageSize,
    offset: (page - 1) * pageSize,
    idCliente: idCliente ? Number(idCliente) : undefined,
    idUsuario: idUsuario || undefined,
    fechaDesde,
    fechaHasta,
    ...sort,
  });

  const { data: stats, isLoading: statsLoading } = useExcepcionesStats({
    idCliente: idCliente ? Number(idCliente) : undefined,
    idUsuario: idUsuario || undefined,
    fechaDesde,
    fechaHasta,
  });

  const { data: usuarios } = useUsersList({ limit: 1000, offset: 0 });
  const canCreate = useHasPermission(Permission.EXCEPCIONES_CREATE);
  const canUpdate = useHasPermission(Permission.EXCEPCIONES_UPDATE);

  const rows = data?.data ?? [];
  const hasFilters =
    idCliente !== '' || idUsuario !== '' || fechaDesde !== undefined || fechaHasta !== undefined;
  const caption = hasFilters ? t('common.segunFiltros') : undefined;
  const statsEmpty = statsLoading && !stats;

  const grupos =
    groupBy === 'none'
      ? null
      : Array.from(
          rows
            .reduce((map, r) => {
              const label =
                groupBy === 'cliente'
                  ? (r.cliente?.nombre ?? `#${r.idCliente}`)
                  : (r.usuario?.username ?? r.usuario?.email ?? '—');
              const grupo = map.get(label);
              if (grupo) grupo.push(r);
              else map.set(label, [r]);
              return map;
            }, new Map<string, CobroExcepcionResponse[]>())
            .entries(),
        ).sort(([a], [b]) => a.localeCompare(b, 'es'));

  if (error) {
    return <EmptyState title={t('pages.excepciones.errorLoad')} />;
  }

  const columns = [
    {
      title: t('common.id'),
      dataIndex: 'id' as keyof CobroExcepcionResponse,
      key: 'id',
      ...serverSortProps('id', sort),
      width: 80,
    },
    {
      title: t('pages.excepciones.cliente'),
      key: 'cliente',
      ...serverSortProps('cliente', sort),
      render: (_: unknown, record: CobroExcepcionResponse) => (
        <span>
          <Avatar
            size={24}
            style={{
              background: token.colorPrimary,
              verticalAlign: 'middle',
              marginRight: 8,
              fontSize: 12,
            }}
            aria-hidden
          >
            {getInitials(record.cliente?.nombre)}
          </Avatar>
          {record.cliente
            ? `${record.cliente.nombre ?? ''} (#${record.cliente.id})`
            : (record.idCliente ?? '-')}
        </span>
      ),
    },
    {
      title: t('pages.excepciones.motivo'),
      dataIndex: 'motivo' as keyof CobroExcepcionResponse,
      key: 'motivo',
      ellipsis: true,
    },
    {
      title: t('pages.excepciones.valor'),
      key: 'valor',
      ...serverSortProps('valor', sort),
      render: (_: unknown, record: CobroExcepcionResponse) => {
        const amount = excepcionAmount(record);
        if (!amount) return '-';
        const num = Number(amount);
        const color = num < 0 ? token.colorError : num > 0 ? token.colorSuccess : undefined;
        return <span style={{ color }}>{formatCurrency(amount)}</span>;
      },
    },
    {
      title: t('pages.excepciones.usuario'),
      key: 'usuario',
      render: (_: unknown, record: CobroExcepcionResponse) =>
        record.usuario ? (record.usuario.username ?? record.usuario.email) : record.idUsuario,
    },
    {
      title: t('pages.excepciones.createdAt'),
      dataIndex: 'fechaCreacion' as keyof CobroExcepcionResponse,
      key: 'fechaCreacion',
      ...serverSortProps('fechaCreacion', sort),
      render: (val: Date) => (val ? dayjs(val).format(DATE_FORMAT) : '-'),
    },
    {
      title: t('common.actions'),
      key: 'actions',
      render: (_: unknown, record: CobroExcepcionResponse) => (
        <Space size={4}>
          <Tooltip title={t('common.ver')}>
            <Button
              type="text"
              icon={<EyeOutlined />}
              aria-label={t('common.ver')}
              onClick={() => setDetailId(record.id)}
            />
          </Tooltip>
          {canUpdate && (
            <Tooltip title={t('common.edit')}>
              <Button
                type="text"
                icon={<EditOutlined />}
                aria-label={t('common.edit')}
                onClick={() => setEditId(record.id)}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ].filter((col) => {
    if (groupBy === 'cliente' && col.key === 'cliente') return false;
    if (groupBy === 'gestor' && col.key === 'usuario') return false;
    return true;
  });

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <KpiRow>
          <KpiCard
            label={t('pages.excepciones.kpis.total')}
            value={String(stats?.total ?? 0)}
            formatValue={false}
            caption={caption}
            empty={statsEmpty}
          />
          <KpiCard
            label={t('pages.excepciones.kpis.valorNeto')}
            value={stats?.valorNeto ?? '0'}
            accent="cobros"
            caption={caption}
            empty={statsEmpty}
          />
          <KpiCard
            label={t('pages.excepciones.kpis.negativas')}
            value={String(stats?.negativas ?? 0)}
            formatValue={false}
            accent="mora"
            caption={caption}
            empty={statsEmpty}
          />
          <KpiCard
            label={t('pages.excepciones.kpis.positivas')}
            value={String(stats?.positivas ?? 0)}
            formatValue={false}
            accent="pagos"
            caption={caption}
            empty={statsEmpty}
          />
        </KpiRow>
      </div>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <Space wrap size={[8, 8]} className="table-filters">
          <Text type="secondary">{t('common.groupBy')}</Text>
          <Segmented
            options={[
              { label: t('common.groupByNone'), value: 'none' },
              { label: t('common.groupByCliente'), value: 'cliente' },
              { label: t('common.groupByGestor'), value: 'gestor' },
            ]}
            value={groupBy}
            onChange={(v) => {
              setGroupBy(v as GroupBy);
              setPage(1);
            }}
          />
          <Input
            placeholder={t('pages.excepciones.searchPlaceholder')}
            prefix={<SearchOutlined />}
            value={idCliente}
            onChange={(e) => {
              setIdCliente(e.target.value);
              setPage(1);
            }}
            style={{ width: 150 }}
            allowClear
          />
          <Select
            placeholder={t('pages.excepciones.userPlaceholder')}
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: 200 }}
            value={idUsuario || undefined}
            onChange={(val) => {
              setIdUsuario(val ?? '');
              setPage(1);
            }}
            options={(usuarios?.data ?? []).map((u) => ({
              label: u.username ?? u.email,
              value: u.id,
            }))}
          />
          <DateRangeFilter
            value={
              fechaDesde !== undefined || fechaHasta !== undefined
                ? [fechaDesde ?? null, fechaHasta ?? null]
                : null
            }
            onChange={(range) => {
              setFechaDesde(range?.[0] ?? undefined);
              setFechaHasta(range?.[1] ?? undefined);
              setPage(1);
            }}
          />
        </Space>
        {canCreate && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            {t('pages.excepciones.create')}
          </Button>
        )}
      </div>
      {grupos ? (
        <>
          {grupos.map(([label, filas]) => (
            <div key={label} style={{ marginBottom: 24 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  marginBottom: 8,
                  borderRadius: 8,
                  background: token.colorFillTertiary,
                  border: `1px solid ${token.colorBorderSecondary}`,
                }}
              >
                {groupBy === 'cliente' && (
                  <Avatar
                    size={24}
                    style={{ background: token.colorPrimary, fontSize: 12 }}
                    aria-hidden
                  >
                    {getInitials(filas[0]?.cliente?.nombre)}
                  </Avatar>
                )}
                <Text strong>{label}</Text>
                <Text type="secondary">{filas.length}</Text>
                <Text type="secondary" style={{ marginLeft: 'auto' }}>
                  {formatCurrency(
                    filas.reduce((acc, r) => acc + Number(excepcionAmount(r) ?? 0), 0).toFixed(2),
                  )}
                </Text>
              </div>
              <DataTable<CobroExcepcionResponse>
                columns={columns}
                dataSource={filas}
                rowKey="id"
                pagination={false}
                onChange={handleChange}
              />
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <Pagination
              current={page}
              pageSize={pageSize}
              total={data?.total ?? 0}
              showSizeChanger={false}
              showTotal={(total, range) =>
                t('pagination.showTotal', { from: range[0], to: range[1], total })
              }
              onChange={(p) => setPage(p)}
            />
          </div>
        </>
      ) : (
        <DataTable<CobroExcepcionResponse>
          columns={columns}
          dataSource={data?.data}
          rowKey="id"
          loading={isLoading}
          locale={{
            emptyText: (
              <EmptyState
                title={t('pages.excepciones.emptyTitle')}
                description={t('pages.excepciones.emptyDescription')}
              />
            ),
          }}
          pagination={{
            current: page,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: false,
            showTotal: (total, range) =>
              t('pagination.showTotal', { from: range[0], to: range[1], total }),
          }}
          onChange={handleChange}
        />
      )}
      <ExcepcionCreateModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={refetch}
      />
      {detailId !== null && (
        <ExcepcionDetailModal id={detailId} onClose={() => setDetailId(null)} />
      )}
      {editId !== null && (
        <ExcepcionEditModal id={editId} onClose={() => setEditId(null)} onSuccess={refetch} />
      )}
    </div>
  );
}
